/**
 * What each reply shows under it: the trust mark, the "Memory & skills used"
 * disclosure, the Assumptions chips, the model badge and the edit-reply pencil.
 *
 * This is ONE decision for the whole SITE, made by its administrator — not a
 * per-person preference. On a shared operations floor the look of a reply is a
 * house style, so it lives server-side (per ERP connection) and everyone signed
 * in to that site sees the same thing. The administrator sets it under
 * Settings → Replies; nobody else can change it. Default: everything shown.
 *
 * Loaded once at app start (router), the same moment the look is loaded. Until
 * it arrives the refs read "on", so a reply is never missing a mark it should
 * have shown.
 */

import { reactive, ref, toRefs } from "vue";
import { api } from "@/lib/api";

export interface ReplyDisplay {
  verified: boolean;
  provenance: boolean;
  assumptions: boolean;
  model: boolean;
  editReply: boolean;
}

const state = reactive<ReplyDisplay>({
  verified: true,
  provenance: true,
  assumptions: true,
  model: true,
  editReply: true,
});

/** True only for the site's administrator — the only person who may change this. */
export const replyDisplayCanEdit = ref(false);

const refs = toRefs(state);
export const showVerified = refs.verified;
export const showProvenance = refs.provenance;
export const showAssumptions = refs.assumptions;
export const showModel = refs.model;
export const showEditReply = refs.editReply;

function apply(value: Partial<ReplyDisplay>): void {
  for (const key of Object.keys(state) as (keyof ReplyDisplay)[]) {
    if (typeof value[key] === "boolean") state[key] = value[key] as boolean;
  }
}

function snapshot(): ReplyDisplay {
  return { verified: state.verified, provenance: state.provenance, assumptions: state.assumptions, model: state.model, editReply: state.editReply };
}

/** Read the site's setting. Called at app start; failure leaves the defaults. */
export async function loadReplyDisplay(): Promise<void> {
  try {
    const value = await api.replyDisplay();
    apply(value);
    replyDisplayCanEdit.value = value.canEdit === true;
  } catch {
    // Signed out, offline, or a server that predates this: everything shows.
  }
}

/**
 * Change one toggle for the whole site (administrator only). Optimistic — the
 * screen updates at once — and reverted if the site refuses it.
 */
export async function setReplyDisplay(key: keyof ReplyDisplay, on: boolean): Promise<void> {
  if (!replyDisplayCanEdit.value) return;
  const previous = state[key];
  state[key] = on;
  try {
    apply(await api.saveReplyDisplay(snapshot()));
  } catch {
    state[key] = previous;
  }
}
